import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { C100Row, ObOsState } from '../../_types/carnivore100.type'
import { c100Tokens, C100Tokens } from './c100-tokens'

const MONO = "'IBM Plex Mono', monospace"

const DASH = (t: C100Tokens) => (
  <Typography sx={{ fontFamily: MONO, fontSize: 13, color: t.muted }}>—</Typography>
)

// A 0-100 screen score. The tint carries the reading at a glance; the number
// stays authoritative, so a colour-blind reader loses nothing.
export function ScoreCell({
  value,
  emphasis = false
}: {
  value: number | null | undefined
  emphasis?: boolean
}) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  if (value == null) return DASH(t)

  const hue = value >= 70 ? t.gain : value >= 45 ? t.accent : t.muted
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 34 }}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: emphasis ? 13.5 : 12.5,
          fontWeight: emphasis ? 600 : 500,
          color: emphasis ? t.text : hue,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1
        }}
      >
        {emphasis ? value.toFixed(1) : value.toFixed(0)}
      </Typography>
      <Box sx={{ height: 2, borderRadius: 1, bgcolor: t.hairline, overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${Math.max(0, Math.min(100, value))}%`,
            bgcolor: hue,
            transition: 'width .3s'
          }}
        />
      </Box>
    </Box>
  )
}

// The (xN) suffix off the sheet: how many of the composite screens surfaced the
// name. One screen is the norm, so it earns no chip.
export function ScreenChip({ count }: { count: number | undefined }) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  if (!count || count < 2) return null
  return (
    <Tooltip title={`Appeared on ${count} of the composite screens`} arrow>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 9.5,
          fontWeight: 600,
          color: t.accent,
          bgcolor: t.faintAccentBg,
          border: `1px solid ${t.accent}55`,
          borderRadius: '5px',
          px: '4px',
          py: '1px',
          lineHeight: 1.6,
          whiteSpace: 'nowrap'
        }}
      >
        ×{count}
      </Box>
    </Tooltip>
  )
}

const OB_OS_LABEL: Record<ObOsState, string> = {
  OB: 'Overbought',
  OS: 'Oversold',
  N: 'Neutral'
}

const HORIZON_LABEL = { st: 'Short term', it: 'Intermediate term', lt: 'Long term' } as const

// The caption under each box. Without it three agreeing horizons render as
// three identical chips and read as a repeated element rather than a reading
// per horizon — the axis has to be legible without hovering for the tooltip.
const HORIZON_ABBR = { st: 'ST', it: 'IT', lt: 'LT' } as const

// Three horizons, short to long. Overbought reads warm, oversold cool, neutral
// recedes — the row's shape is legible before any single letter is read.
export function ObOsCell({ value }: { value: C100Row['obOs'] | undefined }) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  if (!value) return DASH(t)

  const colorOf = (s: ObOsState) => (s === 'OB' ? t.loss : s === 'OS' ? t.gain : t.muted)

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ fontFamily: MONO, fontSize: 11, lineHeight: 1.7 }}>
          {(['st', 'it', 'lt'] as const).map(h => (
            <div key={h}>{`${HORIZON_LABEL[h]}: ${OB_OS_LABEL[value[h]]}`}</div>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'inline-flex', gap: '3px' }}>
        {(['st', 'it', 'lt'] as const).map(h => {
          const state = value[h]
          const color = colorOf(state)
          return (
            <Box
              key={h}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '2px' }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: MONO,
                  fontSize: 9.5,
                  fontWeight: 600,
                  color,
                  bgcolor: state === 'N' ? 'transparent' : `${color}1f`,
                  border: `1px solid ${state === 'N' ? t.hairline : `${color}55`}`,
                  borderRadius: '4px',
                  minWidth: 20,
                  textAlign: 'center',
                  px: '3px',
                  py: '1px',
                  lineHeight: 1.6
                }}
              >
                {state}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: MONO,
                  fontSize: 8,
                  letterSpacing: '0.06em',
                  color: t.muted,
                  textAlign: 'center',
                  lineHeight: 1
                }}
              >
                {HORIZON_ABBR[h]}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Tooltip>
  )
}

// Consecutive up days. Zero is real information (the streak broke), so it is
// shown rather than dashed — just muted.
export function StreakCell({ days }: { days: number | null | undefined }) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  if (days == null) return DASH(t)
  const hot = days >= 3
  return (
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 12.5,
        fontWeight: hot ? 600 : 500,
        color: days === 0 ? t.muted : hot ? t.gain : t.text,
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      {days === 0 ? '0d' : `${days}d`}
    </Typography>
  )
}

// Z's structural trend label. Unknown labels render neutral rather than
// breaking, so a new classification never needs a frontend deploy to be legible.
const TREND_STYLE: Record<string, 'good' | 'neutral' | 'warn'> = {
  'established uptrend': 'good',
  consolidating: 'neutral',
  'extended/peak risk': 'warn'
}

export function TrendLabelCell({ label }: { label: string | null | undefined }) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  if (!label) return DASH(t)

  const kind = TREND_STYLE[label.trim().toLowerCase()] ?? 'neutral'
  const color = kind === 'good' ? t.gain : kind === 'warn' ? t.accent : t.muted

  return (
    <Tooltip title={label} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          maxWidth: 118,
          fontSize: 11,
          fontWeight: 500,
          color,
          bgcolor: `${color}14`,
          border: `1px solid ${color}44`,
          borderRadius: '6px',
          px: '6px',
          py: '2px',
          lineHeight: 1.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Box>
    </Tooltip>
  )
}
